import React, { forwardRef, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react';
import { Close, CloudUpload, Person } from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  Stack,
  TextField,
  Toolbar,
  Typography,
  createTheme,
  styled,
  useMediaQuery,
} from '@mui/material';
import UserStore from '../../store/UserStore';
import AuthStore from '../../store/AuthStore';
import { userRoles } from '../../utils';
import { CustomStyledDialog } from '../../utils/helper';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomButton = styled(Button)({
  backgroundColor: '#F07B0E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#d67e2d',
  },
  width: 'fit-content',
  height: '34px',
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const NewUserDialog = observer(({ showNewUserDialog, handleShowNewUserDialog, userDetails }) => {
  const emptyFormData = {
    id: userDetails?.id ?? null,
    employeeId: userDetails?.employeeId ?? '',
    userName: userDetails?.userName ?? '',
    firstName: userDetails?.firstName ?? '',
    lastName: userDetails?.lastName ?? '',
    emailId: userDetails?.emailId ?? '',
    contactNo: userDetails?.contactNo ?? '',
    address: userDetails?.address ?? '',
    roleCode: userDetails?.roleCode ?? '',
    createdBy: AuthStore.userDetails.Name,
    updatedBy: AuthStore.userDetails.Name,
  };
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);

  const [errors, setErrors] = useState({
    employeeId: false,
    userName: false,
    firstName: false,
    lastName: false,
    emailId: false,
    contactNo: false,
    address: false,
    roleCode: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    employeeId: '',
    userName: '',
    emailId: '',
    contactNo: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    if (name === 'emailId') {
      if (value.length >= 3) {
        UserStore.checkEmailExists(value);
      }
      setErrors({ ...errors, ['emailId']: isEmailIdError(value) });
    } else if (name === 'userName') {
      if (value.length >= 3) {
        UserStore.checkUserNameExists(value);
      }
      setErrors({ ...errors, ['userName']: isUserNameError(value) });
    } else if (name === 'contactNo') {
      setErrors({ ...errors, [name]: isContactNoError(value) });
    } else {
      setErrors({ ...errors, [name]: isEmpty(value) });
    }
  };

  useEffect(() => {
    setEmailExistError();
  }, [UserStore.isEmailExist]);

  useEffect(() => {
    setUserNameExistError();
  }, [UserStore.isUserNameExist]);

  const setEmailExistError = () => {
    setErrors({ ...errors, ['emailId']: UserStore.isEmailExist });
    setErrorMessages({
      ...errors,
      ['emailId']: 'The email is already registered. Please enter different email',
    });
  };

  const setUserNameExistError = () => {
    setErrors({ ...errors, ['userName']: UserStore.isUserNameExist });
    setErrorMessages({
      ...errors,
      ['userName']: 'This username is already taken. Please enter another one.',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      userName: isUserNameError(formData.userName),
      firstName: isEmpty(formData.firstName),
      lastName: isEmpty(formData.lastName),
      emailId: isEmailIdError(formData.emailId),
      contactNo: isContactNoError(formData.contactNo),
      address: isEmpty(formData.address),
      roleCode: isEmpty(formData.roleCode),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      userDetails?.id
        ? UserStore.updateUser({ ...formData })
        : UserStore.createUser({
            ...formData,
            imageBase64: selectedImage?.split(',')[1],
          });
    }
  };

  useEffect(() => {
    (UserStore.createUserStatus || UserStore.updateUserStatus) && handleShowNewUserDialog(false);
  }, [UserStore.createUserStatus || UserStore.updateUserStatus]);

  useEffect(() => {
    if (showNewUserDialog) {
      setFormData(emptyFormData);
      setSelectedImage(null);
    }
  }, [showNewUserDialog]);

  const isEmailIdError = (value) => {
    if (isEmpty(value)) {
      setErrorMessages({ ...errors, ['emailId']: '' });
      return true;
    } else if (!value.includes('@')) {
      setErrorMessages({
        ...errors,
        ['emailId']: 'Please enter the valid emailId',
      });
      return true;
    } else if (UserStore.isEmailExist) {
      setEmailExistError();
      return true;
    } else {
      return false;
    }
  };

  const isContactNoError = (value) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        ['contactNUmber']: 'Please enter valid 10 digit contact number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isUserNameError = (value) => {
    if (isEmpty(value)) {
      return true;
    } else if (UserStore.isUserNameExist) {
      setUserNameExistError();
      return true;
    } else if (value.length < 6) {
      setErrorMessages({
        ...errors,
        ['userName']: 'Username should be min of 6 characters',
      });
      return true;
    } else {
      return false;
    }
  };

  const isEmpty = (value) => {
    return value === null || value === undefined || value.trim() === '';
  };

  const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 36,
    height: 36,
    border: `2px solid ${theme.palette.background.paper}`,
    backgroundColor: '#4c3a22',
    '& :hover': {
      backgroundColor: '#00000045',
    },
  }));

  const CustomAvatar = styled(Avatar)(({ theme }) => ({
    width: 100,
    height: 100,
    backgroundColor: '#bdbdbda1',
    // boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)'
  }));

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  return (
    <CustomStyledDialog
      open={showNewUserDialog}
      onClose={(event, reason) => reason !== 'backdropClick' && handleShowNewUserDialog(false)}
      maxWidth={'xl'}
      noValidate
      TransitionComponent={Transition}
      PaperProps={{
        component: 'form',
        noValidate: true,
        onSubmit: (e) => handleSubmit(e),
      }}
    >
      <DialogTitle>
        <Stack textAlign={'center'}>
          <Typography sx={{ fontWeight: '700' }} component="p">
            {userDetails ? 'Update User' : 'Create New User'}
          </Typography>
        </Stack>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleShowNewUserDialog}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Grid container spacing={3} justifyContent={'space-between'}>
          {userDetails?.id ? (
            ''
          ) : (
            <Grid item xs={12} sm={12} md={12} alignSelf={'center'}>
              <Stack sx={{ py: '10px' }} direction={'column'} spacing={3} alignItems={'center'}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <IconButton component="label" variant="contained">
                      <SmallAvatar alt="Remy Sharp">
                        <CloudUpload />
                        <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileInputChange} />
                      </SmallAvatar>
                    </IconButton>
                  }
                >
                  {selectedImage ? (
                    <CustomAvatar alt={AuthStore.userDetails.Name} src={selectedImage}></CustomAvatar>
                  ) : (
                    <CustomAvatar alt={AuthStore.userDetails.Name}>
                      <Person sx={{ fontSize: 82 }}></Person>
                    </CustomAvatar>
                  )}
                </Badge>
                {selectedImage ? (
                  <Button variant="contained" color="error" onClick={handleRemoveImage}>
                    Remove Image
                  </Button>
                ) : (
                  ''
                )}
              </Stack>
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={12} alignItems="flex-start" justifyContent="flex-start">
            <Stack direction={'column'} spacing={1} sx={{ py: '10px' }}>
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                spacing={1}
                justifyContent={'space-between'}
              >
                <Grid item xs={12} sm={6} md={6} sx={{ pl: '0px !important' }}>
                  <TextField
                    id="outlined-username"
                    name="userName"
                    label="User Name"
                    size="small"
                    value={formData.userName}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.userName)}
                    helperText={errors.userName ? errorMessages.userName : ''}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} sx={{ pl: isSmallScreen ? '0px !important' : '8px' }}>
                  <TextField
                    id="outlined-emailId"
                    name="emailId"
                    label="EmailId"
                    size="small"
                    value={formData.emailId}
                    onChange={handleChange}
                    error={Boolean(errors.emailId)}
                    helperText={errors.emailId ? errorMessages.emailId : ''}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                spacing={1}
                justifyContent={'space-between'}
              >
                <Grid item xs={12} sm={6} md={6} sx={{ pl: '0px !important' }}>
                  <TextField
                    id="outlined-first-name"
                    name="firstName"
                    label="First Name"
                    size="small"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={Boolean(errors.firstName)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} sx={{ pl: isSmallScreen ? '0px !important' : '8px' }}>
                  <TextField
                    id="outlined-last-name"
                    name="lastName"
                    label="Last Name"
                    size="small"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={Boolean(errors.lastName)}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                spacing={1}
                justifyContent={'space-between'}
              >
                <Grid item xs={12} sm={6} md={6} sx={{ pl: '0px !important' }}>
                  <TextField
                    id="outlined-employeeId"
                    name="employeeId"
                    label="Employee Id"
                    size="small"
                    value={formData.employeeId}
                    onChange={(e) => handleChange(e)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} sx={{ pl: isSmallScreen ? '0px !important' : '8px' }}>
                  <TextField
                    id="outlined-contact-number"
                    name="contactNo"
                    label="Contact Number"
                    type="number"
                    fullWidth
                    value={formData.contactNo}
                    onChange={handleChange}
                    error={Boolean(errors.contactNo)}
                    helperText={errors.contactNo ? errorMessages.contactNo : ''}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 0,
                      maxLength: 10,
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                spacing={1}
                justifyContent={'space-between'}
              >
                <Grid item xs={12} sm={12} md={12} sx={{ pl: '0px !important' }}>
                  <FormControl fullWidth size="small" required error={Boolean(errors.roleCode)}>
                    <InputLabel id="demo-simple-select-label">Role</InputLabel>
                    <Select
                      label="Role"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      name="roleCode"
                      value={formData.roleCode}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>Select Role</em>
                      </MenuItem>
                      {userRoles.map((item) => {
                        return (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                spacing={1}
                justifyContent={'space-between'}
              >
                <Grid item xs={12} sm={12} md={12} sx={{ pl: '0px !important' }}>
                  <TextField
                    id="outlined-address"
                    name="address"
                    label="Address"
                    size="small"
                    value={formData.address}
                    onChange={handleChange}
                    error={Boolean(errors.address)}
                    helperText={errors.address}
                    fullWidth
                    multiline
                    rows={3}
                    required
                  />
                </Grid>
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '25px', pt: '0px', justifyContent: 'center' }}>
        <CustomButton autoFocus type="submit">
          {userDetails ? 'Update' : 'Save'}
        </CustomButton>
      </DialogActions>
    </CustomStyledDialog>
  );
});
